/**
 * 今日の過ごし方の好み（睡眠の計画の設定シートの中に出す）
 *
 * 質問に答えると、AI に伝える文章を作って入力欄に入れ、質問は隠す。文章は自由に書き換えられる。
 * 保存すると AI が文章を読み取り、集中・運動の時間の取り方にも使う（文章が正）。
 */

import { Loader2, Sparkles } from 'lucide-react'
import { useState } from 'react'

import type { DayStyleChoices, DayStyleSettings } from '@/api/types/sleep'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import {
  composeDayStyleText,
  DAY_STYLE_NOTE_MAX,
  EXERCISE_OPTIONS,
  EXERCISE_TIME_OPTIONS,
  EXERCISE_TIMING_TIPS,
  FOCUS_OPTIONS,
  type Option,
  RELAX_OPTIONS,
  summarizeChoices,
} from '../day-style-text'

const NOTE_PLACEHOLDER = '例: 頭を使う時間は仕事に使いたい。息抜きは動画。運動は夜にジムで筋トレ。昼寝はしない。'

const Chip = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: string }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      'rounded-full border px-3 py-1 text-xs transition-colors',
      selected
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-background text-foreground hover:bg-muted',
    )}
  >
    {children}
  </button>
)

const Question = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-medium">{title}</p>
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    {children}
  </div>
)

const MultiChoice = <T extends string>({
  options,
  value,
  onChange,
  exclusive,
}: {
  options: Option<T>[]
  value: T[]
  onChange: (value: T[]) => void
  /** これを選ぶと他は外れる（「しない」など） */
  exclusive?: T
}) => {
  const toggle = (v: T) => {
    if (value.includes(v)) return onChange(value.filter((x) => x !== v))
    if (v === exclusive) return onChange([v])
    onChange([...value.filter((x) => x !== exclusive), v])
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <Chip key={o.value} selected={value.includes(o.value)} onClick={() => toggle(o.value)}>
          {o.label}
        </Chip>
      ))}
    </div>
  )
}

/** する／しない。選んでいる方をもう一度押すと未回答に戻す */
const YesNo = ({ value, onChange }: { value: boolean | null; onChange: (value: boolean | null) => void }) => (
  <div className="flex gap-1.5">
    <Chip selected={value === true} onClick={() => onChange(value === true ? null : true)}>
      する
    </Chip>
    <Chip selected={value === false} onClick={() => onChange(value === false ? null : false)}>
      しない
    </Chip>
  </div>
)

const ExerciseTimeGuide = () => (
  <details className="rounded-lg bg-muted/50 px-3 py-2 text-[11px] text-muted-foreground">
    <summary className="cursor-pointer">時間帯ごとの向き・不向き</summary>
    <dl className="mt-1.5 space-y-1">
      {EXERCISE_TIME_OPTIONS.map((o) => (
        <div key={o.value}>
          <dt className="font-medium text-foreground">
            {o.label}（{o.hours}）
          </dt>
          <dd>{o.about}</dd>
        </div>
      ))}
    </dl>
    <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
      <li>選ばなければ、予定の空きと体調から、一番力が出やすい夕方を優先して決めます</li>
      {EXERCISE_TIMING_TIPS.map((tip) => (
        <li key={tip}>{tip}</li>
      ))}
    </ul>
  </details>
)

const Questions = ({
  initial,
  hasText,
  onCompose,
  onCancel,
}: {
  initial: DayStyleChoices
  hasText: boolean
  onCompose: (answers: DayStyleChoices) => void
  onCancel: () => void
}) => {
  const [answers, setAnswers] = useState(initial)
  const update = (patch: Partial<DayStyleChoices>) => setAnswers((a) => ({ ...a, ...patch }))
  const empty = composeDayStyleText(answers) === ''
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        答えると、AI に伝える文章を作ります。あとから自由に書き換えられます（複数選べます。答えなくても使えます）
      </p>
      <Question title="頭を使う時間にしたいことは？">
        <MultiChoice options={FOCUS_OPTIONS} value={answers.focus} onChange={(focus) => update({ focus })} />
      </Question>
      <Question title="息抜き・夜のリラックスは？">
        <MultiChoice options={RELAX_OPTIONS} value={answers.relax} onChange={(relax) => update({ relax })} />
      </Question>
      <Question title="運動はどうしてる？">
        <MultiChoice
          options={EXERCISE_OPTIONS}
          value={answers.exercise}
          onChange={(exercise) => update({ exercise })}
          exclusive="none"
        />
      </Question>
      {!answers.exercise.includes('none') && (
        <Question title="運動しやすい時間は？" hint="空きがあればこの時間帯に運動の時間を取ります">
          <MultiChoice
            options={EXERCISE_TIME_OPTIONS}
            value={answers.exercise_time}
            onChange={(exercise_time) => update({ exercise_time })}
          />
          <ExerciseTimeGuide />
        </Question>
      )}
      <Question title="昼寝はする？">
        <YesNo value={answers.nap} onChange={(nap) => update({ nap })} />
      </Question>
      <Question title="朝や寝る前に復習・暗記はする？" hint="するなら、朝と寝る前に短い復習の時間を取ります">
        <YesNo value={answers.review} onChange={(review) => update({ review })} />
      </Question>
      <div className="flex gap-2">
        <Button size="sm" disabled={empty} onClick={() => onCompose(answers)} className="flex-1">
          <Sparkles />
          {hasText ? 'この答えで文章を作り直す' : '文章にする'}
        </Button>
        {hasText && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            やめる
          </Button>
        )}
      </div>
    </div>
  )
}

export const DayStyleSettingsSection = ({
  value,
  onSave,
}: {
  value: DayStyleSettings
  /** 保存（文章が変わっていれば AI が読み取るので数秒かかる） */
  onSave: (value: DayStyleSettings) => Promise<void>
}) => {
  const [asking, setAsking] = useState(value.note === '')
  const [text, setText] = useState(value.note)
  const [saving, setSaving] = useState(false)
  const read = summarizeChoices(value)

  const save = async (next: DayStyleSettings) => {
    setSaving(true)
    try {
      await onSave(next)
    } finally {
      setSaving(false)
    }
  }

  const handleCompose = (answers: DayStyleChoices) => {
    const composed = composeDayStyleText(answers)
    setText(composed)
    setAsking(false)
    // AI が読み取れなかったときは、この答えをそのまま使う
    void save({ ...answers, note: composed })
  }

  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">今日の過ごし方</h3>
      {asking ? (
        <Questions
          initial={value}
          hasText={value.note !== ''}
          onCompose={handleCompose}
          onCancel={() => setAsking(false)}
        />
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium">AI に伝える、あなたの過ごし方</p>
          <p className="text-[11px] text-muted-foreground">
            自由に書き換えられます。保存すると AI が読み取り、おすすめと集中・運動の時間の取り方に使います
          </p>
          <Textarea
            value={text}
            maxLength={DAY_STYLE_NOTE_MAX}
            placeholder={NOTE_PLACEHOLDER}
            rows={5}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={saving || text.trim() === value.note}
              onClick={() => void save({ ...value, note: text })}
            >
              {saving && <Loader2 className="animate-spin" />}
              {saving ? 'AI が読み取っています…' : '保存'}
            </Button>
            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setAsking(true)}>
              質問に答えて作り直す
            </Button>
          </div>
          <div className="space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-[11px]">
            <p className="font-medium">AI が読み取った内容（時間の取り方に使います）</p>
            {read.length > 0 ? (
              <ul className="flex flex-wrap gap-1">
                {read.map((item) => (
                  <li key={item} className="rounded-full bg-background px-2 py-0.5">
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">まだありません（一般的なおすすめになります）</p>
            )}
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        文章は TODO や予定の名前と一緒に Gemini に送られます。人に知られたくないことは書かないでください。
        変えた内容は、次に今日の過ごし方を作るとき（翌朝か「作り直す」）から反映されます
      </p>
    </section>
  )
}
